import React, { useMemo, useState, useEffect } from 'react';
import { useOcoBrackets } from '@contexts/OcoBracketsContext';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  styled,
  useTheme,
  Switch,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashCan, faPlus, faSpinner, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { AutoOcoBracketModel, AutoOcoBracketRequest, OrderType, AutoOcoBracketValueType, SetAutoOcoBracketsRequest, SetAutoOcoBracketsResult, SetPositionBracketRequest, SetPositionBracketResponse } from '@api/userApi';
import { faCheckCircle, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { useTradingAccount } from '@contexts/TradingAccountContext';
import { useApi } from '@contexts/ApiContext';
import { useSettings } from '@contexts/SettingsContext';
import { toast } from 'react-toastify';
import Modal from '@/components/topstep/modal';
import { useModal } from '@/contexts/ModalContext';
import { ButtonType } from '@/components/topstep/button';

interface AutoOcoBracketsProps {
  onSaved?: () => void;
  onCancelled?: () => void;
  onChangeBracket?: (bracketId: number) => void;
  showButtons?: boolean;
  autoCloseOnSave?: boolean;
  showCloseButton?: boolean;
  selectedBracketId?: number | null;
}

const constrainedTickValue = (value: number | '') => {
  return value === '' ? '' : Math.min(1000, Math.max(1, Math.abs(Math.floor(Number(value)))));
};

const constrainedDollarValue = (value: number | '') => {
  return value === '' ? '' : Math.min(100000, Math.max(1, Number(value)));
};

const AutoOcoBrackets: React.FC<AutoOcoBracketsProps> = ({ onSaved, onCancelled, showButtons = true, autoCloseOnSave = true, showCloseButton = false, selectedBracketId = null, onChangeBracket = null }) => {
  const { brackets, loading, saving, deleteLoading, createBracket, updateBracket, deleteBracket } = useOcoBrackets();
  const { activeTradingAccount, tradingEnvironment } = useTradingAccount();
  const { tradingAccountApi } = useApi();
  const { showModal, hideModal } = useModal();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBracket, setEditingBracket] = useState<AutoOcoBracketModel | null>(null);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [savingPositionBrackets, setSavingPositionBrackets] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [stopLossValue, setStopLossValue] = useState<number | ''>('');
  const [takeProfitValue, setTakeProfitValue] = useState<number | ''>('');
  const [stopLossType, setStopLossType] = useState<OrderType>(OrderType.Stop);
  const [takeProfitType, setTakeProfitType] = useState<OrderType>(OrderType.Limit);
  const [stopLossValueType, setStopLossValueType] = useState<AutoOcoBracketValueType>(AutoOcoBracketValueType.Tick);
  const [takeProfitValueType, setTakeProfitValueType] = useState<AutoOcoBracketValueType>(AutoOcoBracketValueType.Tick);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Position brackets state (risk/toMake settings)
  const [risk, setRisk] = useState<number | null>(activeTradingAccount.bracketAmountToRisk);
  const [toMake, setToMake] = useState<number | null>(activeTradingAccount.bracketAmountToMake);
  const [autoApply, setAutoApply] = useState<boolean>(activeTradingAccount.bracketAutoApply);

  const hasOcoBracketsEnabled = useMemo(() => {
    return activeTradingAccount?.autoOcoBrackets;
  }, [activeTradingAccount]);

  const hasExistingName = useMemo(() => {
    return brackets.some((bracket) => bracket.name === name && bracket.id !== editingBracket?.id);
  }, [brackets, name, editingBracket]);

  // Load settings when component mounts or when settings change
  useEffect(() => {
    setRisk(activeTradingAccount.bracketAmountToRisk);
    setToMake(activeTradingAccount.bracketAmountToMake);
    setAutoApply(activeTradingAccount.bracketAutoApply);
  }, [activeTradingAccount.bracketAmountToRisk, activeTradingAccount.bracketAmountToMake, activeTradingAccount.bracketAutoApply]);

  // Check if position brackets settings have changed
  const isPositionBracketsChanged = useMemo(() => {
    return risk !== activeTradingAccount.bracketAmountToRisk || toMake !== activeTradingAccount.bracketAmountToMake || autoApply !== activeTradingAccount.bracketAutoApply;
  }, [risk, toMake, autoApply, activeTradingAccount.bracketAmountToRisk, activeTradingAccount.bracketAmountToMake, activeTradingAccount.bracketAutoApply]);

  const resetForm = () => {
    setName('');
    setStopLossValue('');
    setTakeProfitValue('');
    setStopLossType(OrderType.Stop);
    setTakeProfitType(OrderType.Limit);
    setStopLossValueType(AutoOcoBracketValueType.Tick);
    setTakeProfitValueType(AutoOcoBracketValueType.Tick);
    setErrors({});
  };

  const openCreateDialog = () => {
    setEditingBracket(null);
    resetForm();
    setOpenDialog(true);
  };

  const openEditDialog = (bracket: AutoOcoBracketModel) => {
    setEditingBracket(bracket);
    setName(bracket.name);
    setStopLossValue(bracket.stopLossValue || '');
    setTakeProfitValue(bracket.takeProfitValue || '');
    setStopLossType(bracket.stopLossType || OrderType.Stop);
    setTakeProfitType(bracket.takeProfitType || OrderType.Limit);
    setStopLossValueType(bracket.stopLossValueType);
    setTakeProfitValueType(bracket.takeProfitValueType);
    setErrors({});
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    //setEditingBracket(null);
    // resetForm();
  };

  const toggleOcoBrackets = async (newMode: boolean) => {
    if (newMode === hasOcoBracketsEnabled) return;

    setSwitchingMode(true);

    try {
      const request = new SetAutoOcoBracketsRequest({
        tradingAccountId: activeTradingAccount.accountId,
        autoOcoBrackets: newMode
      });

      const response = await tradingAccountApi.setAutoOcoBrackets(request);

      if (response.success) {
        // Update the local state
        activeTradingAccount.autoOcoBrackets = newMode;
        toast('Bracket type switched successfully', { type: 'success' });
        onSaved?.();
      } else {
        // Handle specific error cases
        let errorMessage = 'Failed to switch bracket type';

        switch (response.result) {
          case SetAutoOcoBracketsResult.AccountNotFound:
            errorMessage = 'Account not found';
            break;
          case SetAutoOcoBracketsResult.AccountClosed:
            errorMessage = 'Account is closed';
            break;
          case SetAutoOcoBracketsResult.AccountIsFollower:
            errorMessage = 'Cannot switch bracket type on a follower account';
            break;
          case SetAutoOcoBracketsResult.PositionsOpen:
            errorMessage = 'Account must be fully flattened before switching bracket types';
            break;
          case SetAutoOcoBracketsResult.OrdersOnBook:
            errorMessage = 'All orders must be cancelled before switching bracket types';
            break;
          case SetAutoOcoBracketsResult.Exception:
            errorMessage = response.errorMessage || 'An error occurred while switching bracket types';
            break;
          default:
            errorMessage = response.errorMessage || 'Failed to switch bracket type';
        }

        toast(errorMessage, { type: 'error' });
      }
    } catch (error) {
      toast('Error switching bracket type', { type: 'error' });
      console.error('Error switching bracket type:', error);
    } finally {
      setSwitchingMode(false);
    }
  };

  const cancelPositionBrackets = () => {
    setRisk(activeTradingAccount.bracketAmountToRisk);
    setToMake(activeTradingAccount.bracketAmountToMake);
    setAutoApply(activeTradingAccount.bracketAutoApply);
  };

  const savePositionBrackets = async () => {
    setSavingPositionBrackets(true);

    let result: SetPositionBracketResponse | null = null;

    try {
      result = await tradingAccountApi.setPositionBrackets(
        new SetPositionBracketRequest({
          accountId: activeTradingAccount.accountId,
          autoApply: autoApply,
          risk: risk,
          toMake: toMake
        })
      );

      if (result.success) {
        toast('Position brackets saved successfully', { type: 'success' });
        onSaved?.();
      } else {
        toast('Failed to save position brackets: ' + result.errorMessage, { type: 'error' });
      }
    } catch (error) {
      toast('Error saving position brackets', { type: 'error' });
      console.error('Error saving position brackets:', error, result);
    } finally {
      setSavingPositionBrackets(false);
    }
  };

  const CardTitle = styled('p')(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '1.3em',
    fontWeight: 'lighter',
    marginTop: '0.6em',
    marginBottom: '0.6em'
  }));

  //include the selected bracket in the table

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (stopLossValue === '' && takeProfitValue === '') {
      newErrors.general = 'At least one of Stop Loss or Take Profit values must be specified';
    }

    if (stopLossValue !== '') {
      const numValue = Number(stopLossValue);
      if (isNaN(numValue) || numValue <= 0) {
        newErrors.stopLossValue = 'Stop Loss value must be a positive number';
      } else if (stopLossValueType === AutoOcoBracketValueType.Dollar && numValue < 1) {
        newErrors.stopLossValue = 'Stop Loss dollar value must be at least $1';
      } else if (stopLossValueType === AutoOcoBracketValueType.Dollar && numValue > 100000) {
        newErrors.stopLossValue = 'Stop Loss dollar value cannot exceed $100,000';
      }
    }

    if (takeProfitValue !== '') {
      const numValue = Number(takeProfitValue);
      if (isNaN(numValue) || numValue <= 0) {
        newErrors.takeProfitValue = 'Take Profit value must be a positive number';
      } else if (takeProfitValueType === AutoOcoBracketValueType.Dollar && numValue < 1) {
        newErrors.takeProfitValue = 'Take Profit dollar value must be at least $1';
      } else if (takeProfitValueType === AutoOcoBracketValueType.Dollar && numValue > 100000) {
        newErrors.takeProfitValue = 'Take Profit dollar value cannot exceed $100,000';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const request = new AutoOcoBracketRequest({
      name: name.trim(),
      stopLossValue: stopLossValue === '' ? undefined : Number(stopLossValue),
      takeProfitValue: takeProfitValue === '' ? undefined : Number(takeProfitValue),
      stopLossType: stopLossValue === '' ? undefined : stopLossType,
      takeProfitType: takeProfitValue === '' ? undefined : takeProfitType,
      stopLossValueType: stopLossValue === '' ? AutoOcoBracketValueType.Tick : stopLossValueType,
      takeProfitValueType: takeProfitValue === '' ? AutoOcoBracketValueType.Tick : takeProfitValueType,
      id: editingBracket?.id
    });

    const success = editingBracket ? await updateBracket(request) : await createBracket(request);

    if (success) {
      // Always close the create/edit dialog
      closeDialog();
      onSaved?.();
    }
  };

  const handleDelete = async (bracketId: number) => {
    showModal(
      <Modal
        style={{ maxWidth: '600px' }}
        actions={[
          {
            label: 'Cancel',
            onClick: () => {
              hideModal();
            },
            props: { buttonType: ButtonType.red }
          },
          {
            label: 'Delete',
            onClick: async () => {
              hideModal();
              const success = await deleteBracket(bracketId);
            },
            props: { buttonType: ButtonType.green }
          }
        ]}
        header={
          <Typography variant='h4' style={{ color: '#fff' }}>
            Delete Auto OCO Bracket
          </Typography>
        }
      >
        <p style={{ fontSize: '1.2em', lineHeight: '1.5em' }}>Are you sure you want to delete this Auto OCO bracket? This action cannot be undone.</p>
      </Modal>,
      1400
    );
  };

  const getOrderTypeLabel = (orderType: OrderType): string => {
    switch (orderType) {
      case OrderType.Stop:
        return 'Stop Market';
      case OrderType.TrailingStop:
        return 'Trailing Stop Market';
      case OrderType.Limit:
        return 'Limit';
      default:
        return 'Unknown';
    }
  };

  const getValueTypeLabel = (valueType: AutoOcoBracketValueType): string => {
    switch (valueType) {
      case AutoOcoBracketValueType.Tick:
        return 'ticks';
      case AutoOcoBracketValueType.Dollar:
        return 'dollars';
      default:
        return 'unknown';
    }
  };

  const formatValue = (value: number | undefined, valueType: AutoOcoBracketValueType): string => {
    if (value === undefined) return '';
    if (valueType === AutoOcoBracketValueType.Dollar) {
      return `$${value.toFixed(2)}`;
    }
    return `${value} ticks`;
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '60vw', padding: 0, backgroundColor: '#1c1e23' }}>
      <CardHeader
        sx={{ padding: '1em 0' }}
        title={<Typography sx={{ fontSize: '1.3em', fontWeight: 'lighter' }}>{hasOcoBracketsEnabled ? 'Manage Auto OCO Brackets' : 'Position Risk / Profit Brackets'}</Typography>}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title='Account must be fully flattened before switching'>
              <Button variant='contained' color='primary' onClick={() => toggleOcoBrackets(!hasOcoBracketsEnabled)} disabled={switchingMode} startIcon={switchingMode ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}>
                {switchingMode ? 'Switching...' : hasOcoBracketsEnabled ? 'Switch to Position Brackets' : 'Switch to Auto OCO Brackets'}
              </Button>
            </Tooltip>
            {showCloseButton && (
              <Button variant='outlined' onClick={onCancelled}>
                Close
              </Button>
            )}
          </Box>
        }
      />
      <Box>
        {hasOcoBracketsEnabled ? (
          // Auto OCO Brackets view
          <Box>
            <Typography variant='body1' color='textSecondary' sx={{ padding: '1em 0' }}>
              When enabled, Auto OCO Brackets automatically place linked Take Profit and Stop Loss orders per each entry order. These brackets adjust based on the entry price and allow multiple orders at varying levels, rather than aggregating them into a
              single order at the same price.
            </Typography>
            {brackets.length === 0 ? (
              <Box textAlign='center' py={4}>
                <Typography variant='body1' color='textSecondary'>
                  No Auto OCO brackets found. Create your first bracket to get started.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ backgroundColor: '#1c1e23', marginTop: '1em' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Stop Loss</TableCell>
                      <TableCell>Take Profit</TableCell>
                      <TableCell align='right'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {brackets.map((bracket) => (
                      <TableRow
                        key={bracket.id}
                        sx={(theme) => {
                          return {
                            backgroundColor: bracket.id === selectedBracketId ? theme.palette.primary.dark : theme.palette.darkBg,
                            '&:hover': {
                              backgroundColor: bracket.id === selectedBracketId ? theme.palette.primary.dark : theme.palette.darkBg
                            }
                          };
                        }}
                      >
                        <TableCell>
                          <Typography variant='body1' fontWeight='medium'>
                            {bracket.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {bracket.stopLossValue ? (
                            <Typography variant='body2'>
                              {formatValue(bracket.stopLossValue, bracket.stopLossValueType)} ({getOrderTypeLabel(bracket.stopLossType || OrderType.Stop)})
                            </Typography>
                          ) : (
                            <Typography variant='body2' color='textSecondary'>
                              Not set
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {bracket.takeProfitValue ? (
                            <Typography variant='body2'>
                              {formatValue(bracket.takeProfitValue, bracket.takeProfitValueType)} ({getOrderTypeLabel(bracket.takeProfitType || OrderType.Limit)})
                            </Typography>
                          ) : (
                            <Typography variant='body2' color='textSecondary'>
                              Not set
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                            {selectedBracketId != bracket.id && onChangeBracket && (
                              <IconButton title='Use Bracket' onClick={() => onChangeBracket(bracket.id)} size='small'>
                                <FontAwesomeIcon icon={faPlus} />
                              </IconButton>
                            )}
                            <IconButton title='Edit Bracket' onClick={() => openEditDialog(bracket)} size='small'>
                              <FontAwesomeIcon icon={faEdit} />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(bracket.id)} size='small' disabled={deleteLoading === bracket.id}>
                              {deleteLoading === bracket.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrashCan} />}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : (
          // Position Brackets view (risk/toMake settings)
          <Box sx={{ p: 2 }}>
            <Typography variant='body1' color='textSecondary' sx={{ mb: 3 }}>
              Position Brackets automatically aggregate your Take Profit and Stop Loss orders at the same price, functioning as a single bracket. Multiple entry and exit orders are not supported on Position Brackets.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Risk (~$)' variant='outlined' value={risk || ''} type='number' InputProps={{ inputProps: { min: 0 } }} onChange={(e) => setRisk(e.target.value === '' ? null : Number(e.target.value))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label='Profit (~$)' variant='outlined' value={toMake || ''} type='number' InputProps={{ inputProps: { min: 0 } }} onChange={(e) => setToMake(e.target.value === '' ? null : Number(e.target.value))} />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel control={<Checkbox checked={autoApply} onChange={(e) => setAutoApply(e.target.checked)} />} label='Automatically apply Risk / Profit bracket to new Positions' />
            </Box>
          </Box>
        )}
      </Box>

      {/* Action Buttons at Bottom */}
      {showButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
          {hasOcoBracketsEnabled ? (
            <Button variant='contained' startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={openCreateDialog}>
              Add Bracket
            </Button>
          ) : (
            isPositionBracketsChanged && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant='outlined' onClick={cancelPositionBrackets} disabled={savingPositionBrackets}>
                  Cancel
                </Button>
                <Button variant='contained' onClick={savePositionBrackets} disabled={savingPositionBrackets} startIcon={savingPositionBrackets ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}>
                  {savingPositionBrackets ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            )
          )}
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={closeDialog} maxWidth='sm' fullWidth>
        <DialogTitle color='text.primary'>{editingBracket ? 'Edit Auto OCO Bracket' : 'Create Auto OCO Bracket'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {errors.general && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {errors.general}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField inputProps={{ maxLength: 64 }} fullWidth label='Bracket Name' value={name} onChange={(e) => setName(e.target.value)} error={!!errors.name} helperText={errors.name} required />
                {hasExistingName && (
                  <Typography variant='body2' color='error'>
                    A bracket with this name already exists.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={`Stop Loss ${stopLossValueType === AutoOcoBracketValueType.Dollar ? 'Dollars' : 'Ticks'}`}
                  type='number'
                  value={stopLossValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (stopLossValueType === AutoOcoBracketValueType.Tick) {
                      setStopLossValue(value === '' ? '' : constrainedTickValue(Number(value)));
                    } else {
                      setStopLossValue(value === '' ? '' : constrainedDollarValue(Number(value)));
                    }
                  }}
                  error={!!errors.stopLossValue}
                  helperText={errors.stopLossValue}
                  inputProps={{
                    min: 1,
                    step: stopLossValueType === AutoOcoBracketValueType.Tick ? 1 : 0.01,
                    max: stopLossValueType === AutoOcoBracketValueType.Dollar ? 100000 : 1000
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Stop Loss Value Type</InputLabel>
                  <Select
                    value={stopLossValueType}
                    onChange={(e) => {
                      setStopLossValueType(e.target.value as AutoOcoBracketValueType);
                      setStopLossValue(''); // Reset value when changing type
                    }}
                    label='SL Value Type'
                  >
                    <MenuItem value={AutoOcoBracketValueType.Tick}>Ticks</MenuItem>
                    <MenuItem value={AutoOcoBracketValueType.Dollar}>Dollars</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Stop Loss Order Type</InputLabel>
                  <Select value={stopLossType} onChange={(e) => setStopLossType(e.target.value as OrderType)} label='Stop Loss Order Type'>
                    <MenuItem value={OrderType.Stop}>Stop Market</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={`Take Profit ${takeProfitValueType === AutoOcoBracketValueType.Dollar ? 'Dollars' : 'Ticks'}`}
                  type='number'
                  value={takeProfitValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (takeProfitValueType === AutoOcoBracketValueType.Tick) {
                      setTakeProfitValue(value === '' ? '' : constrainedTickValue(Number(value)));
                    } else {
                      setTakeProfitValue(value === '' ? '' : constrainedDollarValue(Number(value)));
                    }
                  }}
                  error={!!errors.takeProfitValue}
                  helperText={errors.takeProfitValue}
                  inputProps={{
                    min: 1,
                    step: takeProfitValueType === AutoOcoBracketValueType.Tick ? 1 : 0.01,
                    max: takeProfitValueType === AutoOcoBracketValueType.Dollar ? 100000 : 1000
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Take Profit Value Type</InputLabel>
                  <Select
                    value={takeProfitValueType}
                    onChange={(e) => {
                      setTakeProfitValueType(e.target.value as AutoOcoBracketValueType);
                      setTakeProfitValue(''); // Reset value when changing type
                    }}
                    label='TP Value Type'
                  >
                    <MenuItem value={AutoOcoBracketValueType.Tick}>Ticks</MenuItem>
                    <MenuItem value={AutoOcoBracketValueType.Dollar}>Dollars</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Take Profit Order Type</InputLabel>
                  <Select value={takeProfitType} onChange={(e) => setTakeProfitType(e.target.value as OrderType)} label='Take Profit Order Type'>
                    <MenuItem value={OrderType.Limit}>Limit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant='contained' disabled={saving || !name || hasExistingName || (stopLossValue === '' && takeProfitValue === '')} startIcon={saving ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}>
            {saving ? 'Saving...' : editingBracket ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoOcoBrackets;
